import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Lock, User, Eye, EyeOff, LogOut, Settings, Users, Package, BarChart3, ShoppingBag, Calendar, Home, Layers, ShoppingCart, Ticket } from 'lucide-react';
import { AuthService } from '../firebase';
import ProductManagement from '../components/ProductManagement';
import AppointmentManagement from '../components/AppointmentManagement';
import HomepageManagement from '../components/HomepageManagement';
import CollectionManagement from '../components/CollectionManagement';
import OrderManagement from '../components/OrderManagement';
import CouponManagement from '../components/CouponManagement';

const AdminContainer = styled.div`
  min-height: 100vh;
  background: #f7f7f7;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
`;

const LoginCard = styled.div`
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  border-radius: 3px;
  padding: 40px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
  width: 100%;
  max-width: 400px;
`;

const AdminPanel = styled.div`
  background: #f8f9fa;
  min-height: 100vh;
`;

const AdminHeader = styled.div`
  background: #fff;
  padding: calc(var(--header-h) + 30px) 0 20px 0;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  margin-bottom: 0;
`;

const AdminContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 40px 20px 20px 20px;
`;

const LoginTitle = styled.h1`
  text-align: center;
  color: #333;
  margin-bottom: 30px;
  font-size: 2rem;
  font-weight: 300;
`;

const InputGroup = styled.div`
  margin-bottom: 20px;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 8px;
  color: #555;
  font-weight: 500;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px 15px;
  border: 2px solid #e1e5e9;
  border-radius: 3px;
  font-size: 16px;
  transition: border-color 0.3s ease;

  &:focus {
    outline: none;
    border-color: #1a1a1a;
  }
`;

const PasswordInput = styled.div`
  position: relative;
`;

const PasswordToggle = styled.button`
  position: absolute;
  right: 15px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  cursor: pointer;
  color: #666;
`;

const LoginButton = styled.button`
  width: 100%;
  background: #1a1a1a;
  color: white;
  border: none;
  padding: 15px;
  border-radius: 3px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: transform 0.3s ease;

  &:hover:not(:disabled) {
    transform: translateY(-2px);
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`;

const ErrorMessage = styled.div`
  color: #dc3545;
  margin-bottom: 20px;
  text-align: center;
`;

const TestCredentials = styled.div`
  margin-top: 20px;
  text-align: center;
  font-size: 14px;
  color: #666;
`;

const TabNavigation = styled.div`
  display: flex;
  background: white;
  border-radius: 3px;
  padding: 5px;
  margin-bottom: 20px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
`;

const TabButton = styled.button<{ active: boolean }>`
  flex: 1;
  padding: 12px 20px;
  border: none;
  background: ${props => props.active ? '#007bff' : 'transparent'};
  color: ${props => props.active ? 'white' : '#666'};
  border-radius: 8px;
  cursor: pointer;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: all 0.3s ease;

  &:hover {
    background: ${props => props.active ? '#0056b3' : '#f8f9fa'};
  }
`;

const TabContent = styled.div`
  background: white;
  border-radius: 3px;
  padding: 20px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
`;

const WelcomeContainer = styled.div`
  background: white;
  padding: 30px;
  border-radius: 3px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
`;

const WelcomeTitle = styled.h2`
  color: #333;
  margin-bottom: 20px;
`;

const WelcomeText = styled.p`
  color: #666;
  line-height: 1.6;
`;

const AdminHeaderContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const AdminTitle = styled.h1`
  color: #333;
  font-size: 1.8rem;
  font-weight: 300;
`;

const LogoutButton = styled.button`
  background: #dc3545;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: background 0.3s ease;

  &:hover {
    background: #c82333;
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
  margin-bottom: 30px;
`;

const StatCard = styled.div`
  background: white;
  padding: 30px;
  border-radius: 3px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
  text-align: center;
  transition: transform 0.3s ease;

  &:hover {
    transform: translateY(-5px);
  }

  .icon {
    width: 60px;
    height: 60px;
    background: #1a1a1a;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 20px;
    color: white;
  }

  h3 {
    font-size: 2rem;
    font-weight: 700;
    color: #333;
    margin-bottom: 10px;
  }

  p {
    color: #666;
    font-size: 1rem;
  }
`;

// Admin paneline girebilecek hesaplar.
// NOT: Buradaki kontrol yalnizca arayuz icindir. Asil denetim Firestore
// guvenlik kurallarinda ve /api/upload/presign endpoint'indedir; tarayicidaki
// hicbir kontrol tek basina guvenlik saglamaz.
const ADMIN_EMAILS = ['ysfasan04@gmail.com', 'sultanasan16@gmail.com'];

function isAdminUser(user?: { email?: string | null } | null): boolean {
  const email = (user?.email || '').toLowerCase();
  return email.length > 0 && ADMIN_EMAILS.includes(email);
}

const AdminPage: React.FC = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('dashboard');

  // Check auth state on component mount
  useEffect(() => {
    const unsubscribe = AuthService.onAuthStateChanged((user) => {
      // Sadece yetkili hesaplar panele girebilir; kayitli olmak yetmez
      setIsLoggedIn(isAdminUser(user));
    });

    return () => unsubscribe();
  }, []);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    // Giris yalnizca Firebase Authentication uzerinden yapilir.
    const result = await AuthService.signIn(username.trim(), password);

    if (!result.success) {
      setError('E-posta veya şifre hatalı!');
    } else if (!isAdminUser(result.user)) {
      // Kayitli ama yetkisiz hesap: oturumu hemen kapat
      await AuthService.signOut();
      setError('Bu hesabın yönetim paneline erişim yetkisi yok.');
    } else {
      setIsLoggedIn(true);
      setError('');
    }
    
    setLoading(false);
  };

  const handleLogout = async () => {
    setLoading(true);
    await AuthService.signOut();
    setIsLoggedIn(false);
    setUsername('');
    setPassword('');
    setLoading(false);
  };

  if (!isLoggedIn) {
    return (
      <AdminContainer>
        <LoginCard>
          <LoginTitle>
            <Lock size={30} style={{ marginRight: '10px', verticalAlign: 'middle' }} />
            Admin Panel
          </LoginTitle>
          
          <form onSubmit={handleLogin}>
            <InputGroup>
              <Label>E-posta</Label>
              <Input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="ornek@mail.com"
                required
              />
            </InputGroup>

            <InputGroup>
              <Label>Şifre</Label>
              <PasswordInput>
                <Input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="admin123"
                  required
                />
                <PasswordToggle onClick={() => setShowPassword(!showPassword)}>
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </PasswordToggle>
              </PasswordInput>
            </InputGroup>

            {error && (
              <ErrorMessage>
                {error}
              </ErrorMessage>
            )}

            <LoginButton type="submit" disabled={loading}>
              <User size={20} style={{ marginRight: '10px' }} />
              {loading ? 'Giriş Yapılıyor...' : 'Giriş Yap'}
            </LoginButton>
          </form>

          <TestCredentials>
            <strong>Test Kullanıcısı:</strong><br />
            Kullanıcı Adı: admin<br />
            Şifre: admin123
          </TestCredentials>
        </LoginCard>
      </AdminContainer>
    );
  }

  return (
    <AdminPanel>
      <AdminHeader>
        <AdminHeaderContent>
          <AdminTitle>
            <Settings size={30} style={{ marginRight: '10px', verticalAlign: 'middle' }} />
            Admin Panel
          </AdminTitle>
          <LogoutButton onClick={handleLogout} disabled={loading}>
            <LogOut size={20} />
            {loading ? 'Çıkış Yapılıyor...' : 'Çıkış Yap'}
          </LogoutButton>
        </AdminHeaderContent>
      </AdminHeader>

      <AdminContent>
        <TabNavigation>
          <TabButton 
            active={activeTab === 'dashboard'} 
            onClick={() => setActiveTab('dashboard')}
          >
            <BarChart3 size={20} />
            Dashboard
          </TabButton>
          <TabButton 
            active={activeTab === 'homepage'} 
            onClick={() => setActiveTab('homepage')}
          >
            <Home size={20} />
            Ana Sayfa
          </TabButton>
          <TabButton 
            active={activeTab === 'collections'} 
            onClick={() => setActiveTab('collections')}
          >
            <Layers size={20} />
            Koleksiyonlar
          </TabButton>
          <TabButton 
            active={activeTab === 'products'} 
            onClick={() => setActiveTab('products')}
          >
            <ShoppingBag size={20} />
            Ürün Yönetimi
          </TabButton>
          <TabButton 
            active={activeTab === 'appointments'} 
            onClick={() => setActiveTab('appointments')}
          >
            <Calendar size={20} />
            Randevu Yönetimi
          </TabButton>
          <TabButton 
            active={activeTab === 'orders'} 
            onClick={() => setActiveTab('orders')}
          >
            <ShoppingCart size={20} />
            Sipariş Yönetimi
          </TabButton>
          <TabButton
            active={activeTab === 'coupons'}
            onClick={() => setActiveTab('coupons')}
          >
            <Ticket size={20} />
            Kupon Yönetimi
          </TabButton>
        </TabNavigation>

        <TabContent>
          {activeTab === 'dashboard' && (
            <>
              <StatsGrid>
                <StatCard>
                  <div className="icon">
                    <Users size={30} />
                  </div>
                  <h3>156</h3>
                  <p>Toplam Müşteri</p>
                </StatCard>

                <StatCard>
                  <div className="icon">
                    <Package size={30} />
                  </div>
                  <h3>89</h3>
                  <p>Toplam Ürün</p>
                </StatCard>

                <StatCard>
                  <div className="icon">
                    <BarChart3 size={30} />
                  </div>
                  <h3>24</h3>
                  <p>Bu Ay Satış</p>
                </StatCard>
              </StatsGrid>

              <WelcomeContainer>
                <WelcomeTitle>Hoş Geldiniz!</WelcomeTitle>
                <WelcomeText>
                  Admin paneline başarıyla giriş yaptınız. Buradan site içeriğini yönetebilir, 
                  ürünleri ekleyebilir ve müşteri bilgilerini görüntüleyebilirsiniz.
                </WelcomeText>
              </WelcomeContainer>
            </>
          )}

          {activeTab === 'homepage' && <HomepageManagement />}

          {activeTab === 'collections' && <CollectionManagement />}

          {activeTab === 'products' && <ProductManagement />}

          {activeTab === 'appointments' && <AppointmentManagement />}

          {activeTab === 'orders' && <OrderManagement />}

          {activeTab === 'coupons' && <CouponManagement />}

          {activeTab === 'users' && (
            <div>
              <h2>Kullanıcı Yönetimi</h2>
              <p>Kullanıcı yönetimi özellikleri yakında eklenecek.</p>
            </div>
          )}
        </TabContent>
      </AdminContent>
    </AdminPanel>
  );
};

export default AdminPage;
